import { keepPreviousData, useQuery } from '@tanstack/react-query'
import { useMemo, useState } from 'react'
import { licensedDevicesQuery, licenseRowsQuery, licensesTableQuery, unlicensedDevicesQuery } from '../api/softsee'
import { deviceIcon } from '../alarms/AlarmDetails'
import { Content } from '../ui/Shell'
import { DataTable, useTableState } from '../ui/DataTable'
import { Spinner } from '../ui/Spinner'
import { licenseColumns } from './columns'

/**
 * Softsee::licenses - `#softwareLicenses`, cu filele randului: licentele,
 * statiile licentiate si cele nelicentiate. Toate trei sunt de citit.
 *
 * Panoul "License Gaps" de deasupra tabelului NU mai e: era un desen amCharts,
 * iar amCharts a iesit din portal sub auditul bancar. Ramasese aici cutia goala
 * de 180 de pixeli in care desena.
 */

type Tab = 'licenses' | 'licensed' | 'unlicensed'

interface DeviceTile {
  deviceId: number
  Denumire: string
  Tip: number
  type_name?: string | null
  Nume?: string | null
  Prenume?: string | null
}

const LICENSE_TABS: { key: Tab; label: string; className?: string }[] = [
  { key: 'licenses', label: 'Licenses' },
  { key: 'licensed', label: 'Devices with license' },
  { key: 'unlicensed', label: 'Devices without license', className: ' color-red' },
]

/** `allowed`: filele pe care omul le are - actionTabs.php le punea sub `canAccess()`. */
function LicenseTabs({ id, allowed }: { id: number; allowed?: string[] }) {
  const tabs = allowed ? LICENSE_TABS.filter((t) => allowed.includes(t.key)) : LICENSE_TABS
  const [tab, setTab] = useState<Tab | null>(tabs[0]?.key ?? null)
  const rows = useQuery({ ...licenseRowsQuery(id), enabled: tab === 'licenses' })
  const licensed = useQuery({ ...licensedDevicesQuery(id), enabled: tab === 'licensed' })
  const unlicensed = useQuery({ ...unlicensedDevicesQuery(id), enabled: tab === 'unlicensed' })
  const busy =
    (tab === 'licenses' && rows.isPending) ||
    (tab === 'licensed' && licensed.isPending) ||
    (tab === 'unlicensed' && unlicensed.isPending)

  return (
    <div className="row d-flex m-0">
      <div className="col-auto bg-lightest-gray">
        <ul className="action_tabs">
          {tabs.map((one) => (
            <li key={one.key}>
              <a
                href="#"
                className={`action_button${one.className ?? ''}${tab === one.key ? ' active' : ''}`}
                onClick={(e) => {
                  e.preventDefault()
                  setTab(one.key)
                }}
              >
                {one.label}
              </a>
            </li>
          ))}
        </ul>
      </div>
      <div className="col-md-12">
        <div className="details_content">
          {tab === null && <p className="text-muted">You do not have access to any of these details.</p>}
          {busy && <Spinner />}
          {tab === 'licenses' && rows.data && <LicenseRows rows={rows.data.data} />}
          {tab === 'licensed' && licensed.data && (
            <DeviceTiles title="Licensed" devices={licensed.data.data as unknown as DeviceTile[]} licensed />
          )}
          {tab === 'unlicensed' && unlicensed.data && (
            <DeviceTiles title="Unlicensed" devices={unlicensed.data.data as unknown as DeviceTile[]} licensed={false} />
          )}
        </div>
      </div>
    </div>
  )
}

/** licenses/details/licenses.php - ApplicationLicensesModel. */
function LicenseRows({ rows }: { rows: Record<string, unknown>[] }) {
  return (
    <>
      <header className="panel-heading sm" data-color="theme-inverse">
        <h4>
          <strong>Licenses</strong>
        </h4>
      </header>
      <div className="panel-body">
        {rows.length === 0 ? (
          <div className="alert alert-info">
            <span className="fas fa-info-circle"></span> No licenses.
          </div>
        ) : (
          <table className="table table-striped table-hover licenses-table" width="100%" id="licensesTable">
            <thead>
              <tr>
                <th className="a-left">License Type</th>
                <th className="a-left">Licenses</th>
                <th className="a-left">Active licenses</th>
                <th className="a-left">Start date</th>
                <th className="a-left">Status</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={String(row.ID)}>
                  <td className="td-small a-left">
                    {row.type_name ? (
                      <span className="label label-primary">{String(row.type_name)}</span>
                    ) : (
                      <span className="label label-default">Not licensed</span>
                    )}
                  </td>
                  <td className="td-small a-left">{row.licenses_number ? String(row.licenses_number) : ''}</td>
                  <td className="td-small a-left">{row.active_licenses ? String(row.active_licenses) : ''}</td>
                  <td className="td-small a-left">{formatDay(row.start_date)}</td>
                  <td className="td-small a-left">
                    <LicenseStatus status={String(row.status ?? '')} end={row.end_date} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </>
  )
}

function formatDay(value: unknown): string {
  const text = String(value ?? '').slice(0, 10)
  if (text === '' || text.startsWith('0000')) return ''
  const when = new Date(`${text}T00:00:00`)
  if (Number.isNaN(when.getTime())) return text
  return when.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
}

/**
 * Aceeasi pastila ca in model: verde cat mai tine, rosie cand a trecut. Fara
 * data de sfarsit nu se scrie nimic - o licenta pe viata nu expira.
 */
function LicenseStatus({ status, end }: { status: string; end: unknown }) {
  const day = formatDay(end)
  if (day === '') return null
  if (status === 'valid') {
    return (
      <span className="color-quartz" style={{ border: '1px solid #089FA8', padding: '5px 10px', borderRadius: 3 }}>
        expires {day}
      </span>
    )
  }
  return (
    <span className="color-red" style={{ border: '1px solid #d83430', padding: '5px 10px', borderRadius: 3 }}>
      expired
    </span>
  )
}

/** licenses/details/licensedDevices.php si unlicensedDevices.php. */
function DeviceTiles({ title, devices, licensed }: { title: string; devices: DeviceTile[]; licensed: boolean }) {
  return (
    <>
      <header className="panel-heading sm" data-color="theme-inverse">
        <h4>
          <strong>{title}</strong> devices
        </h4>
      </header>
      <div className="panel-body">
        <div className="row">
          {devices.length === 0 ? (
            <div className="alert alert-danger">
              <span className="fas fa-info-circle"></span> No devices.
            </div>
          ) : (
            devices.map((device) => (
              <div className="col-md-4" key={device.deviceId}>
                <div className="tiles-2 bg-normal">
                  <div className="image-box">
                    <div className="image">
                      <i className={`${deviceIcon(device.Tip)} rounded ${licensed ? 'success' : 'danger'}`}></i>
                    </div>
                  </div>
                  <div className="content">
                    <div className="title">
                      <strong>{device.Denumire}</strong>
                    </div>
                    <div className="details">
                      <span className="fas fa-user"></span> {String(device.Prenume ?? '').toUpperCase()}{' '}
                      {device.Nume ?? ''}
                    </div>
                    {licensed ? (
                      <div className="details">
                        <span className="fa fa-star color-warning"></span> {device.type_name ?? ''}
                      </div>
                    ) : (
                      <div className="details color-red">
                        <span className="far fa-star"></span> no license
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </>
  )
}

export function LicensesPage() {
  const columns = useMemo(() => licenseColumns(), [])
  const [state, setState] = useTableState('softwareLicenses', { start: 0, length: 25, order: { column: 1, dir: 'asc' }, search: '' })
  const orderColumn = columns.find((c) => c.index === state.order.column) ?? columns[1]
  const query = useQuery({
    ...licensesTableQuery({
      draw: 1,
      start: state.start,
      length: state.length,
      orderColumn: orderColumn.index,
      orderName: orderColumn.name ?? '',
      orderDir: state.order.dir,
      search: state.search,
    }),
    placeholderData: keepPreviousData,
  })

  return (
    <Content>
      <div id="content">
        <DataTable
          id="softwareLicenses"
          columnTools
          columns={columns}
          rows={query.data?.data ?? []}
          total={query.data?.recordsTotal ?? 0}
          fetching={query.isFetching}
          error={query.isError ? (query.error as Error).message : null}
          onRetry={() => void query.refetch()}
          state={state}
          onState={setState}
          rowId={(r) => String(r.DT_RowId)}
          details={(row) => <LicenseTabs id={Number(row.ID)} allowed={query.data?.tabs} />}
        />
      </div>
    </Content>
  )
}
